/** @format */

import React from 'react';

const TOOLTIP_GAP = 10;
const VIEWPORT_PADDING = 8;

const isTouchLikeDevice = () => {
  if (typeof window === 'undefined') return false;

  return (
    window.matchMedia('(hover: none), (pointer: coarse)').matches ||
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0
  );
};

export const Tooltip = ({ content, placement = 'top', maxLines = 3, triggerAriaLabel = 'More information' }) => {
  const wrapperRef = React.useRef(null);
  const tooltipRef = React.useRef(null);
  const tooltipIdRef = React.useRef(`tooltip-${Math.random().toString(36).slice(2, 10)}`);
  const touchDeviceRef = React.useRef(isTouchLikeDevice());
  const [isOpen, setIsOpen] = React.useState(false);
  const [currentPlacement, setCurrentPlacement] = React.useState(placement);

  React.useEffect(() => {
    setCurrentPlacement(placement);
  }, [placement]);

  React.useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsidePointer = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = event => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsidePointer);
    document.addEventListener('touchstart', handleOutsidePointer, { passive: true });
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsidePointer);
      document.removeEventListener('touchstart', handleOutsidePointer);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen || !wrapperRef.current || !tooltipRef.current) return;

    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    let safePlacement = placement;

    if (placement === 'top' && tooltipRect.top < VIEWPORT_PADDING) safePlacement = 'bottom';
    if (placement === 'bottom' && tooltipRect.bottom > window.innerHeight - VIEWPORT_PADDING) safePlacement = 'top';
    if (placement === 'left' && tooltipRect.left < VIEWPORT_PADDING) safePlacement = 'right';
    if (placement === 'right' && tooltipRect.right > window.innerWidth - VIEWPORT_PADDING) safePlacement = 'left';

    const isVerticalPlacement = safePlacement === 'top' || safePlacement === 'bottom';
    if (isVerticalPlacement) {
      const halfWidth = tooltipRect.width / 2;
      if (wrapperRect.left + wrapperRect.width / 2 - halfWidth < VIEWPORT_PADDING) safePlacement = 'right';
      if (wrapperRect.left + wrapperRect.width / 2 + halfWidth > window.innerWidth - VIEWPORT_PADDING)
        safePlacement = 'left';
    } else {
      const halfHeight = tooltipRect.height / 2;
      if (wrapperRect.top + wrapperRect.height / 2 - halfHeight < VIEWPORT_PADDING) safePlacement = 'bottom';
      if (wrapperRect.top + wrapperRect.height / 2 + halfHeight > window.innerHeight - VIEWPORT_PADDING)
        safePlacement = 'top';
    }

    setCurrentPlacement(safePlacement);
  }, [isOpen, placement]);

  const openTooltip = () => {
    setIsOpen(true);
  };

  const closeTooltip = () => {
    setIsOpen(false);
  };

  const handleBlur = event => {
    if (wrapperRef.current && wrapperRef.current.contains(event.relatedTarget)) return;
    closeTooltip();
  };

  const handleClick = () => {
    if (!touchDeviceRef.current) return;
    setIsOpen(open => !open);
  };

  const handleMouseEnter = () => {
    if (touchDeviceRef.current) return;
    openTooltip();
  };

  const handleMouseLeave = () => {
    if (touchDeviceRef.current) return;
    closeTooltip();
  };

  return (
    <span ref={wrapperRef} className="cp-tooltip">
      <button
        type="button"
        className="cp-tooltip__trigger"
        aria-label={triggerAriaLabel}
        aria-describedby={isOpen ? tooltipIdRef.current : undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={openTooltip}
        onBlur={handleBlur}
        onClick={handleClick}
      >
        <span className="cp-tooltip__trigger-icon" aria-hidden="true" />
      </button>
      {isOpen && (
        <span
          id={tooltipIdRef.current}
          ref={tooltipRef}
          className={`cp-tooltip__bubble cp-tooltip__bubble--${currentPlacement}`}
          role="tooltip"
          style={{ '--max-lines': maxLines, '--tooltip-gap': `${TOOLTIP_GAP}px` }}
        >
          <span className="cp-tooltip__bubble-content">{content}</span>
        </span>
      )}
    </span>
  );
};
